/**
 * Onboarding Wizard — /projects/new
 * Four steps: Project (name + country + location), Capacity, Profile, Scope reveal.
 * Country and location lead because they decide which questions are worth asking;
 * the old standalone Intent screen is folded into the Profile step (the
 * interconnection answer already carries the topology half of it).
 */
import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ChevronRight, ChevronLeft, Zap, CheckCircle, Sparkles } from "lucide-react";
import { api, checkupSnapshot, recordWaitlist, clearCheckupToken, peekCheckupToken, resolveCheckupPrefill, type CheckupSnapshot, type ScopePreview } from "../lib/api.ts";
import { COUNTRY_META } from "../lib/constants.ts";
import { CountryBadge } from "../components/shared/CountryBadge.tsx";
import { getCapacityStep, getCommercialOptions, deriveIntent } from "../lib/checkup-options.ts";
import { formatCapacity } from "../lib/utils.ts";
import { ALL_COUNTRIES, isLive, COMING_SOON_BLURB, isValidEmail, REQUEST_EMAIL_MAX, REQUEST_NOTE_MAX } from "../lib/checkup-coverage.ts";

type CountryCode = "KE" | "NG" | "ZA" | "GH" | "NA" | "ZM";

interface WizardState {
  name: string;
  country: CountryCode | null;
  capacity_kw: number | null;
  intent: string;
  location: string;
  /** Optional site pin, "lat, lon" decimal degrees → S-001/S-002 on create. */
  site_coords: string;
  // Project profile — the attributes conditional requirements test against
  // (null = "not sure": dependent requirements stay visible, flagged to confirm).
  interconnection_mode: string | null;
  imports_generator: boolean | null;
  imports_equipment: boolean | null;
  hybrid_or_fuel_fired: boolean | null;
  has_battery_storage: boolean | null;
  local_assembly_or_manufacture: boolean | null;
  employs_expatriates: boolean | null;
  site_flags: string[];
  programme_participation: string[];
}

const INTERCONNECTION_OPTIONS: { value: string; label: string; hint: string }[] = [
  { value: "isolated-mini-grid", label: "Isolated mini-grid", hint: "Standalone system, no DisCo connection" },
  { value: "interconnected-mini-grid", label: "Interconnected mini-grid", hint: "Mini-grid connected to a DisCo network" },
  { value: "grid-tied-DisCo", label: "Grid-tied (distribution)", hint: "Connects at a distribution company's network" },
  { value: "grid-tied-transmission", label: "Grid-tied (transmission)", hint: "Connects at transmission level (TCN)" },
  { value: "behind-the-meter-C&I", label: "Behind-the-meter C&I", hint: "On-site supply to a commercial/industrial customer" },
];

/** Parse "lat, lon" in decimal degrees; null when the text isn't coordinates. */
function parseCoords(text: string): { lat: number; lon: number } | null {
  const m = text.trim().match(/^(-?\d{1,2}(?:\.\d+)?)\s*,\s*(-?\d{1,3}(?:\.\d+)?)$/);
  if (!m) return null;
  const lat = Number(m[1]);
  const lon = Number(m[2]);
  if (lat < -90 || lat > 90 || lon < -180 || lon > 180) return null;
  return { lat, lon };
}

const SITE_FLAG_OPTIONS: { value: string; label: string }[] = [
  { value: "near_airport", label: "Near an airport / controlled airspace" },
  { value: "near_military", label: "Near a military installation" },
  { value: "protected_heritage", label: "On or near protected heritage land" },
  { value: "water_abstraction", label: "Will draw water (cooling/cleaning at scale)" },
];

const PROFILE_QUESTIONS: { key: keyof WizardState; label: string; hint: string }[] = [
  { key: "imports_generator", label: "Importing a generator?", hint: "Generating sets need a NERC clearance certificate at customs" },
  { key: "imports_equipment", label: "Importing PV equipment?", hint: "Modules, inverters, balance-of-system — drives SONCAP + customs clearance" },
  { key: "has_battery_storage", label: "Battery storage?", hint: "Battery regulations (PRO registration, environmental audit chain)" },
  { key: "hybrid_or_fuel_fired", label: "Hybrid / fuel-fired component?", hint: "Diesel or gas backup, or CSP" },
  { key: "local_assembly_or_manufacture", label: "Locally assembled or manufactured equipment?", hint: "Locally made solar products need MANCAP instead of SONCAP" },
  { key: "employs_expatriates", label: "Employing expatriate staff?", hint: "Expatriate quota via the Ministry of Interior" },
];

export function OnboardingWizard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // URL params are the primary carrier of the checkup capability, but they die on the
  // magic-link hop (the email opens a fresh tab, and the post-auth redirect that would have
  // rebuilt them lived in that tab's empty sessionStorage). The localStorage stash survives
  // the hop — fall back to it, so "Create New Project" right after a checkup sign-in still
  // pre-fills. Read the stash ONLY when the URL carries no token: `stashedToken` being set
  // means "the stash is the source", which is what gates the single-shot clear below — a
  // URL-token visit (the original login tab, LoginRedirect) must never spend the stash the
  // magic-link tab still needs. Frozen at mount: the stash mutates during auth.
  const [stashedToken] = useState(() =>
    searchParams.get("checkup_token") ? undefined : peekCheckupToken());
  const { fromCheckup, checkupToken } = resolveCheckupPrefill(
    searchParams.get("from"), searchParams.get("checkup_token"), stashedToken);
  const [step, setStep] = useState(0);
  const [state, setState] = useState<WizardState>({
    name: "",
    country: null,
    capacity_kw: 5000,
    intent: "",
    location: "",
    site_coords: "",
    interconnection_mode: null,
    imports_generator: null,
    imports_equipment: null,
    hybrid_or_fuel_fired: null,
    has_battery_storage: null,
    local_assembly_or_manufacture: null,
    employs_expatriates: null,
    site_flags: [],
    programme_participation: [],
  });
  const [creating, setCreating] = useState(false);
  const [scaffolding, setScaffolding] = useState<string[]>([]);
  const [snapshot, setSnapshot] = useState<CheckupSnapshot | null>(null);
  const [scope, setScope] = useState<ScopePreview | null>(null);
  const [scopeLoading, setScopeLoading] = useState(false);
  // The coming-soon country a developer clicked to request. null = the request panel is closed.
  const [requestCountry, setRequestCountry] = useState<CountryCode | null>(null);

  // Fetch checkup snapshot if coming from checkup CTA. The token is the ONLY key the server
  // resolves by (bearer capability from the CTA URL — never the account email, which an
  // attacker can sign up as; PR #63 review). No token → the server would answer null anyway,
  // so don't bother asking.
  useEffect(() => {
    if (!fromCheckup || !checkupToken) return;
    checkupSnapshot.get(checkupToken).then(({ snapshot: snap }) => {
      if (!snap) return;
      setSnapshot(snap);
      setState(prev => ({
        ...prev,
        country: (snap.country as CountryCode) || prev.country,
        capacity_kw: snap.capacity_kw || prev.capacity_kw,
        intent: snap.intent || prev.intent,
        location: snap.fields?.["S-003"] || prev.location,
      }));
      // The stash is single-shot: its snapshot is applied, so the client-side capability is
      // spent — a stale (or planted) stash must not keep pre-filling later visits. But only
      // when the stash was the SOURCE: the original login tab can reach this wizard too
      // (LoginRedirect carries the URL token when the session broadcasts across tabs), and
      // clearing from that tab would race the magic-link tab's own read of the stash — the
      // exact blank wizard this branch fixes. The token lives on in component state for the
      // post-create server-side clear either way.
      if (stashedToken) clearCheckupToken();
    }).catch(() => {});
  }, [fromCheckup, checkupToken, stashedToken]);

  const steps = ["Project", "Capacity", "Profile", "Scope"];

  // The intent the wizard actually submits: mini-grid interconnection answers derive it,
  // a single compatible commercial option auto-selects, otherwise it is the developer's
  // pick from the "Who is the power for?" question on the Profile step.
  const derivedIntent = state.country && state.capacity_kw
    ? deriveIntent(state.country, state.capacity_kw, state.interconnection_mode, state.intent)
    : "";
  const commercialOptions = state.country && state.capacity_kw
    ? getCommercialOptions(state.country, state.capacity_kw, state.interconnection_mode)
    : [];

  /** The profile payload shared by createProject and the scope preview. */
  function wizardAttributes() {
    return {
      interconnection_mode: state.interconnection_mode,
      imports_generator: state.imports_generator,
      imports_equipment: state.imports_equipment,
      hybrid_or_fuel_fired: state.hybrid_or_fuel_fired,
      has_battery_storage: state.has_battery_storage,
      local_assembly_or_manufacture: state.local_assembly_or_manufacture,
      employs_expatriates: state.employs_expatriates,
      site_flags: state.site_flags,
      programme_participation: state.programme_participation,
      state_region: state.location || null,
    };
  }

  // Live scope preview — the same licence routing createProject will run, fetched when
  // the developer reaches the Scope step. On failure the Licenses tile falls back to the
  // static routing estimate (Fields/Months show "—"); creation is never blocked.
  useEffect(() => {
    if (step !== 3 || !state.country || !state.capacity_kw) return;
    let cancelled = false;
    setScopeLoading(true);
    api.scopePreview({
      country_code: state.country,
      capacity_kw: state.capacity_kw,
      intent: derivedIntent || undefined,
      attributes: wizardAttributes(),
    })
      .then((res) => { if (!cancelled) setScope(res); })
      .catch(() => { if (!cancelled) setScope(null); })
      .finally(() => { if (!cancelled) setScopeLoading(false); });
    return () => { cancelled = true; };
  }, [step]);

  function getRoutingResult(): { tier: string; color: string; licenses: number } {
    if (!state.country || !state.capacity_kw) return { tier: "—", color: "gray", licenses: 0 };
    const kw = state.capacity_kw;

    if (state.country === "KE") {
      if (kw <= 1000 && derivedIntent === "own_use") return { tier: "Exempt", color: "brand", licenses: 2 };
      if (kw <= 1000) return { tier: "Net Metering", color: "warning", licenses: 3 };
      return { tier: "Full License (EPRA)", color: "ai", licenses: 10 };
    }
    if (state.country === "NG") {
      if (kw <= 100) return { tier: "Registration", color: "brand", licenses: 2 };
      if (kw <= 1000) return { tier: "Mini-Grid Permit", color: "warning", licenses: 4 };
      return { tier: "Full License (NERC)", color: "ai", licenses: 8 };
    }
    if (state.country === "ZA") {
      if (derivedIntent === "private_offtaker") return { tier: "NERSA Registration", color: "brand", licenses: 8 };
      return { tier: "Full License (NERSA)", color: "ai", licenses: 10 };
    }
    if (state.country === "GH") {
      if (kw <= 500) return { tier: "Simplified Permit (EC)", color: "brand", licenses: 4 };
      return { tier: "Wholesale Generation License (EC)", color: "ai", licenses: 12 };
    }
    if (state.country === "NA") {
      if (kw <= 500) return { tier: "Net Metering (ECB)", color: "brand", licenses: 3 };
      return { tier: "Full Licence (ECB)", color: "ai", licenses: 8 };
    }
    if (state.country === "ZM") {
      if (kw < 250 && derivedIntent === "own_use") return { tier: "Exempt (Own Use)", color: "brand", licenses: 1 };
      if (kw < 250) return { tier: "Net Metering (ERB)", color: "warning", licenses: 3 };
      return { tier: "Full Licence (ERB)", color: "ai", licenses: 8 };
    }
    return { tier: "—", color: "gray", licenses: 0 };
  }

  async function handleCreate() {
    if (!state.country || !state.capacity_kw || !state.name) return;
    setCreating(true);

    const scaffoldSteps = [
      "Creating project record...",
      "Building license graph from seed data...",
      "Resolving field definitions for country...",
      "Generating document checklist...",
      "Associating submission templates...",
      "Computing critical path + parallel workplan...",
      "Running initial evaluation...",
    ];

    // Animate scaffolding
    for (let i = 0; i < scaffoldSteps.length; i++) {
      await new Promise((r) => setTimeout(r, 400));
      setScaffolding((prev) => [...prev, scaffoldSteps[i]!]);
    }

    try {
      const result = await api.createProject({
        name: state.name,
        country_code: state.country,
        capacity_kw: state.capacity_kw,
        intent: derivedIntent || undefined,
        attributes: wizardAttributes(),
      });

      // Persist the site pin when one was given (S-001/S-002 — decimal degrees).
      // createProject already writes S-003 from state_region; no second write here.
      const coords = parseCoords(state.site_coords);
      if (coords) {
        await api.batchUpdateFields(result.projectId, [
          { field_id: "S-001", value: String(coords.lat) },
          { field_id: "S-002", value: String(coords.lon) },
        ]).catch(() => {});
      }

      // Pre-populate fields from checkup snapshot
      if (snapshot?.mapped_fields) {
        const updates = Object.entries(snapshot.mapped_fields)
          .filter(([id]) => id !== "P-002" && id !== "C-001" && id !== "C-002")
          .map(([field_id, value]) => ({ field_id, value }));
        if (updates.length > 0) {
          await api.batchUpdateFields(result.projectId, updates).catch(() => {});
        }
      }
      // The snapshot served its purpose the moment a project exists — clear it even when it
      // carried no mapped_fields, so checkup answers don't linger on the waitlist row.
      if (snapshot && checkupToken) {
        await checkupSnapshot.clear(checkupToken).catch(() => {});
      }
      // Same for the local stash: a later sign-in or "Create New Project" must start blank,
      // not resurrect this checkup.
      clearCheckupToken();

      await new Promise((r) => setTimeout(r, 600));
      navigate(`/projects/${result.projectId}`);
    } catch (e) {
      console.error(e);
      setCreating(false);
    }
  }

  const routing = getRoutingResult();

  return (
    <div className="max-w-xl mx-auto px-4 py-8">
      <h1 className="text-[22px] font-bold mb-1" style={{ letterSpacing: "-0.02em" }}>Create New Project</h1>
      <p className="text-[14px] text-text-secondary mb-6">
        Tell us about your solar project and we'll map your regulatory pathway.
      </p>

      {/* Checkup pre-fill banner */}
      {snapshot && (
        <div className="flex items-start gap-2.5 p-3 bg-ai-light border border-ai/20 rounded-xl mb-6">
          <Sparkles size={16} className="text-ai flex-shrink-0 mt-0.5" />
          <p className="text-[13px] text-text-secondary">
            Your checkup answers have been pre-filled. Enter a project name to get started.
          </p>
        </div>
      )}

      {/* Step indicators */}
      <div className="flex items-center gap-1 mb-8 overflow-x-auto pb-1">
        {steps.map((s, i) => (
          <div key={s} className="flex items-center gap-1 flex-shrink-0">
            <div
              className={`w-6 h-6 rounded-full text-[12px] font-medium flex items-center justify-center ${
                i < step ? "bg-brand text-on-solid" : i === step ? "bg-brand text-on-solid" : "bg-surface-tertiary text-text-tertiary"
              }`}
            >
              {i < step ? <CheckCircle size={12} /> : i + 1}
            </div>
            <span className={`text-[13px] hidden sm:inline ${i === step ? "text-text-primary font-medium" : "text-text-tertiary"}`}>
              {s}
            </span>
            {i < steps.length - 1 && <ChevronRight size={12} className="text-text-tertiary mx-1" />}
          </div>
        ))}
      </div>

      {creating ? (
        /* Scaffolding sequence */
        <div className="bg-surface border border-border rounded-2xl p-5">
          <h2 className="text-[17px] font-medium mb-4">Setting up your project...</h2>
          <div className="space-y-2.5">
            {scaffolding.map((s, i) => (
              <div key={i} className="flex items-center gap-2 text-[14px]">
                <CheckCircle size={14} className="text-brand flex-shrink-0" />
                <span className="text-text-secondary">{s}</span>
              </div>
            ))}
          </div>
        </div>
      ) : (
        /* Wizard steps */
        <div className="bg-surface border border-border rounded-2xl p-5">
          {/* Step 0: Project name + Country + Location. Country and location come first
              because they decide which questions the later steps ask (state-specific
              requirements, DisCo vs county programmes). */}
          {step === 0 && (
            <div className="space-y-4">
              <div>
                <label className="block text-[13px] font-medium text-text-secondary uppercase tracking-wider mb-1.5">
                  Project Name
                </label>
                <input
                  type="text"
                  value={state.name}
                  onChange={(e) => setState({ ...state, name: e.target.value })}
                  placeholder="e.g., Naivasha Solar Farm"
                  className="w-full px-3 py-2 border border-border rounded-xl text-[16px] focus:outline-none focus:ring-1 focus:ring-brand/30"
                />
              </div>
              <div>
                <label className="block text-[13px] font-medium text-text-secondary uppercase tracking-wider mb-2">
                  Country
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {ALL_COUNTRIES.map((code) => {
                    const live = isLive(code);
                    return (
                      <button
                        key={code}
                        onClick={() => {
                          // Live countries create a project; coming-soon ones can't (no verified
                          // regulatory set) — clicking opens the "request this regulatory set" panel
                          // and never selects the country.
                          if (live) {
                            setRequestCountry(null);
                            setState({ ...state, country: code });
                          } else {
                            setRequestCountry(code);
                          }
                        }}
                        aria-label={live ? COUNTRY_META[code].name : `${COUNTRY_META[code].name} — coming soon. Request this regulatory set.`}
                        className={`relative flex flex-col items-center gap-1.5 p-3 rounded-xl border transition-colors ${
                          !live
                            ? "border-dashed border-border bg-surface-secondary hover:border-brand"
                            : state.country === code
                              ? "border-brand bg-brand-light"
                              : "border-border hover:border-border-secondary"
                        }`}
                      >
                        <span className={`text-2xl ${live ? "" : "opacity-45 grayscale"}`}>{COUNTRY_META[code].flag}</span>
                        <span className={`text-[14px] font-medium ${live ? "" : "text-text-secondary"}`}>{COUNTRY_META[code].name}</span>
                        {!live && (
                          <span className="text-[10px] font-medium uppercase tracking-wider text-text-tertiary">Coming soon</span>
                        )}
                      </button>
                    );
                  })}
                </div>
                <p className="mt-2 text-[12px] text-text-tertiary">
                  Nigeria is live today. Kenya and South Africa are next — click one to tell us you need it and we'll prioritise it.
                </p>
              </div>

              {requestCountry && (
                <CountryRequestPanel
                  country={requestCountry}
                  onClose={() => setRequestCountry(null)}
                />
              )}
              {state.country && (
                <>
                  <div>
                    <label className="block text-[13px] font-medium text-text-secondary uppercase tracking-wider mb-1.5">
                      {{ KE: "County", NG: "State", ZA: "Province", GH: "Region", NA: "Region", ZM: "Province" }[state.country]}
                    </label>
                    <input
                      type="text"
                      value={state.location}
                      onChange={(e) => setState({ ...state, location: e.target.value })}
                      placeholder={{ KE: "e.g., Nakuru", NG: "e.g., Lagos", ZA: "e.g., Northern Cape", GH: "e.g., Greater Accra", NA: "e.g., Erongo", ZM: "e.g., Lusaka" }[state.country]}
                      className="w-full px-3 py-2 border border-border rounded-xl text-[16px] focus:outline-none focus:ring-1 focus:ring-brand/30"
                    />
                    {state.country === "ZA" && state.location.toLowerCase().includes("northern cape") && (
                      <div className="mt-2 p-2.5 bg-danger-light border border-danger/20 rounded-xl text-[13px] text-danger">
                        Northern Cape has ZERO available grid capacity since GCCA-2024. Consider Western Cape or Eastern Cape.
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-[13px] font-medium text-text-secondary uppercase tracking-wider mb-1.5">
                      Site Coordinates <span className="normal-case font-normal">(optional)</span>
                    </label>
                    <input
                      type="text"
                      value={state.site_coords}
                      onChange={(e) => setState({ ...state, site_coords: e.target.value })}
                      placeholder="lat, lon — e.g., -1.2921, 36.8219"
                      className="w-full px-3 py-2 border border-border rounded-xl text-[16px] focus:outline-none focus:ring-1 focus:ring-brand/30"
                    />
                    {state.site_coords.trim() !== "" && !parseCoords(state.site_coords) && (
                      <p className="text-[12px] text-text-tertiary mt-1.5">
                        Enter decimal degrees as "latitude, longitude" — or leave this blank and name the area above.
                      </p>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Step 1: Capacity */}
          {step === 1 && (
            <div className="space-y-4">
              <div>
                <label className="block text-[13px] font-medium text-text-secondary uppercase tracking-wider mb-1.5">
                  Plant Capacity
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    value={state.capacity_kw ?? ""}
                    onChange={(e) => {
                      if (e.target.value === "") {
                        setState({ ...state, capacity_kw: null });
                        return;
                      }
                      const kw = Math.round(Number(e.target.value));
                      if (!Number.isFinite(kw)) return;
                      setState({ ...state, capacity_kw: Math.max(kw, 1) });
                    }}
                    placeholder="50"
                    step={getCapacityStep(state.capacity_kw ?? 5000)}
                    min="0"
                    className="w-32 px-3 py-2 border border-border rounded-xl text-[16px] focus:outline-none focus:ring-1 focus:ring-brand/30"
                  />
                  <span className="text-[16px] text-text-secondary">kW (AC export)</span>
                </div>
                {state.capacity_kw !== null && state.capacity_kw >= 1000 && (
                  <p className="text-[12px] text-text-tertiary mt-1.5">= {formatCapacity(state.capacity_kw)}</p>
                )}
              </div>

              {state.capacity_kw && state.country && (
                <div className={`p-3 rounded-xl border text-[14px] ${
                  routing.color === "brand" ? "bg-brand-light border-brand/20 text-brand" :
                  routing.color === "warning" ? "bg-warning-light border-warning/20 text-warning-text" :
                  "bg-ai-light border-ai/20 text-ai"
                }`}>
                  <div className="font-medium">{routing.tier}</div>
                  <div className="opacity-80 mt-0.5">~{routing.licenses} licenses/permits required</div>
                </div>
              )}

              {state.country === "ZA" && (
                <p className="text-[13px] text-text-tertiary">
                  In South Africa, licensing depends on off-taker type more than capacity — we'll determine this in the next step.
                </p>
              )}
              {state.country === "GH" && (
                <p className="text-[13px] text-text-tertiary">
                  In Ghana, projects ≤500 kW qualify for a simplified Energy Commission permit. Larger projects require a Wholesale Generation License and PURC tariff approval.
                </p>
              )}
            </div>
          )}

          {/* Step 2: Project profile — drives conditional applicability. Absorbs the old
              Intent screen: the interconnection answer IS the topology half of "intended
              use"; a commercial question appears only when more than one arrangement is
              still compatible with the chosen mode. */}
          {step === 2 && state.country && (
            <div className="space-y-4">
              <div>
                <label className="block text-[13px] font-medium text-text-secondary uppercase tracking-wider mb-2">
                  How will the project connect?
                </label>
                <div className="space-y-2">
                  {INTERCONNECTION_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => setState({ ...state, interconnection_mode: opt.value })}
                      className={`w-full text-left px-3 py-2 rounded-xl border text-[14px] transition-colors ${
                        state.interconnection_mode === opt.value
                          ? "border-brand bg-brand-light"
                          : "border-border hover:border-border-secondary"
                      }`}
                    >
                      <span className="font-medium">{opt.label}</span>
                      <span className="block text-[12px] text-text-tertiary">{opt.hint}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Only once the mode is answered — before that this would just replay the
                  old Intent list, mini-grid duplicates and all. */}
              {state.interconnection_mode && commercialOptions.length > 1 && (
                <div>
                  <label className="block text-[13px] font-medium text-text-secondary uppercase tracking-wider mb-2">
                    Who is the power for?
                  </label>
                  <div className="space-y-2">
                    {commercialOptions.map((opt) => (
                      <button
                        key={opt.value}
                        onClick={() => setState({ ...state, intent: opt.value })}
                        className={`w-full text-left px-3 py-2 rounded-xl border text-[14px] transition-colors ${
                          derivedIntent === opt.value
                            ? "border-brand bg-brand-light"
                            : "border-border hover:border-border-secondary"
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <label className="block text-[13px] font-medium text-text-secondary uppercase tracking-wider mb-2">
                  Project profile
                </label>
                <p className="text-[12px] text-text-tertiary mb-2">
                  These switch specific requirements on or off. "Not sure" keeps them visible, flagged for you to confirm later.
                </p>
                <div className="space-y-2">
                  {PROFILE_QUESTIONS.map((q) => (
                    <div key={q.key} className="flex items-center justify-between gap-2 px-3 py-2 rounded-xl border border-border">
                      <div className="min-w-0">
                        <div className="text-[14px] font-medium">{q.label}</div>
                        <div className="text-[12px] text-text-tertiary truncate">{q.hint}</div>
                      </div>
                      <div className="flex gap-1 flex-shrink-0">
                        {([["Yes", true], ["No", false], ["?", null]] as const).map(([lbl, val]) => (
                          <button
                            key={lbl}
                            onClick={() => setState({ ...state, [q.key]: val } as WizardState)}
                            className={`px-2.5 py-1 rounded-lg text-[13px] border ${
                              (state[q.key] as boolean | null) === val
                                ? "border-brand bg-brand-light font-medium"
                                : "border-border text-text-secondary hover:border-border-secondary"
                            }`}
                            title={lbl === "?" ? "Not sure" : lbl}
                          >
                            {lbl}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-[13px] font-medium text-text-secondary uppercase tracking-wider mb-2">
                  Site screening (tick any that apply)
                </label>
                <div className="space-y-1.5">
                  {SITE_FLAG_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => setState({
                        ...state,
                        site_flags: state.site_flags.includes(opt.value)
                          ? state.site_flags.filter(f => f !== opt.value)
                          : [...state.site_flags, opt.value],
                      })}
                      className={`w-full text-left px-3 py-2 rounded-xl border text-[14px] transition-colors ${
                        state.site_flags.includes(opt.value)
                          ? "border-brand bg-brand-light"
                          : "border-border hover:border-border-secondary"
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {state.country === "NG" && (
                <div>
                  <label className="block text-[13px] font-medium text-text-secondary uppercase tracking-wider mb-2">
                    Programme participation
                  </label>
                  <button
                    onClick={() => setState({
                      ...state,
                      programme_participation: state.programme_participation.includes("DARES")
                        ? state.programme_participation.filter(x => x !== "DARES")
                        : [...state.programme_participation, "DARES"],
                    })}
                    className={`w-full text-left px-3 py-2 rounded-xl border text-[14px] transition-colors ${
                      state.programme_participation.includes("DARES")
                        ? "border-brand bg-brand-light"
                        : "border-border hover:border-border-secondary"
                    }`}
                  >
                    <span className="font-medium">DARES participant</span>
                    <span className="block text-[12px] text-text-tertiary">World Bank Distributed Access through Renewable Energy Scale-up — programme rules apply only to participants</span>
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Step 3: Scope Reveal — live numbers from /api/projects/scope-preview (the same
              routing createProject runs), not hardcoded estimates. */}
          {step === 3 && (
            <div>
              <h2 className="text-[17px] font-medium mb-3">Your Regulatory Pathway</h2>
              <div className="grid grid-cols-3 gap-2 mb-2">
                <div className="bg-surface-secondary rounded-xl p-2 sm:p-2.5 text-center">
                  <div className="text-[18px] sm:text-[22px] font-medium">
                    {scopeLoading ? "…" : scope ? scope.licenses_required + scope.licenses_conditional : `~${routing.licenses}`}
                  </div>
                  <div className="text-[11px] sm:text-[12px] text-text-tertiary uppercase">Licenses</div>
                </div>
                <div className="bg-surface-secondary rounded-xl p-2 sm:p-2.5 text-center">
                  <div className="text-[18px] sm:text-[22px] font-medium">
                    {scopeLoading ? "…" : scope ? scope.fields_total : "—"}
                  </div>
                  <div className="text-[11px] sm:text-[12px] text-text-tertiary uppercase">Fields</div>
                </div>
                <div className="bg-surface-secondary rounded-xl p-2 sm:p-2.5 text-center">
                  <div className="text-[18px] sm:text-[22px] font-medium">
                    {scopeLoading ? "…" : scope?.est_months != null ? `~${scope.est_months}` : "—"}
                  </div>
                  <div className="text-[11px] sm:text-[12px] text-text-tertiary uppercase">Months Est.</div>
                </div>
              </div>
              {scope && !scopeLoading && (
                <p className="text-[12px] text-text-tertiary mb-4">
                  {scope.licenses_required} confirmed required
                  {scope.licenses_conditional > 0 && <> · {scope.licenses_conditional} conditional — answers on your profile decide these</>}
                  {scope.licenses_under_review > 0 && <> · {scope.licenses_under_review} under regulatory review on our side</>}
                  {scope.est_months != null && <> · timeline is the statutory processing floor, not a promise</>}
                </p>
              )}

              {state.country && (
                <CountryBadge code={state.country} showName />
              )}

              <div className="mt-3 text-[14px] text-text-secondary">
                <p className="mb-2">
                  <strong>{state.name}</strong> — {formatCapacity(state.capacity_kw ?? 0)} {routing.tier}
                </p>
                <p>
                  We'll create your project, build the license dependency graph, resolve all field definitions for{" "}
                  {state.country && COUNTRY_META[state.country].name}, and generate your workplan.
                </p>
              </div>
            </div>
          )}

          {/* Navigation */}
          <div className="flex items-center justify-between mt-6 pt-4 border-t border-border">
            {step > 0 ? (
              <button
                onClick={() => setStep(step - 1)}
                className="flex items-center gap-1 text-[14px] text-text-secondary hover:text-text-primary"
              >
                <ChevronLeft size={14} /> Back
              </button>
            ) : (
              <button
                onClick={() => navigate("/projects")}
                className="text-[14px] text-text-secondary hover:text-text-primary"
              >
                Cancel
              </button>
            )}

            {step < 3 ? (
              <button
                onClick={() => setStep(step + 1)}
                disabled={
                  (step === 0 && (!state.name || !state.country)) ||
                  (step === 1 && !state.capacity_kw)
                }
                className="flex items-center gap-1 px-4 py-1.5 bg-brand text-on-solid rounded-xl text-[16px] font-medium disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Next <ChevronRight size={14} />
              </button>
            ) : (
              <button
                onClick={handleCreate}
                className="flex items-center gap-1.5 px-4 py-1.5 bg-brand text-on-solid rounded-xl text-[16px] font-medium"
              >
                <Zap size={14} /> Create project
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * "Request this regulatory set" — the demand signal for a coming-soon country, from inside the
 * wizard. Same waitlist write the checkup uses (recordWaitlist, source: "regulatory_request"),
 * carrying the developer's own words as the research brief. We never dead-end on our own plumbing:
 * the request is recorded, or the developer is told plainly.
 */
function CountryRequestPanel({ country, onClose }: { country: CountryCode; onClose: () => void }) {
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");
  const [status, setStatus] = useState<"idle" | "sending" | "done" | "error">("idle");
  const valid = isValidEmail(email);

  async function submit() {
    if (!valid) return;
    setStatus("sending");
    const recorded = await recordWaitlist({ source: "regulatory_request", email, country, note }).catch(() => false);
    setStatus(recorded ? "done" : "error");
  }

  if (status === "done") {
    return (
      <div className="rounded-xl border border-brand bg-brand-light p-4">
        <p className="text-[15px] font-medium">Noted — you want {COUNTRY_META[country].name}.</p>
        {/* No "we'll email you": there is no mailer in this codebase, and we don't promise what
            isn't shipped. The recorded request — with the email on it — is the promise we keep. */}
        <p className="mt-1 text-[14px] text-text-secondary">
          Requests are how we choose which regulatory set to build next. Yours is recorded, with your email on it, and it counts.
        </p>
        <button className="mt-3 text-[14px] font-medium text-brand underline" onClick={onClose}>Pick another country</button>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-border bg-surface p-4">
      <div className="flex items-start gap-2">
        <span className="text-xl opacity-45 grayscale">{COUNTRY_META[country].flag}</span>
        <div className="flex-1">
          <p className="text-[15px] font-medium">{COUNTRY_META[country].name} isn't built yet</p>
          <p className="mt-1 text-[14px] text-text-secondary">{COMING_SOON_BLURB}</p>
        </div>
      </div>
      <div className="mt-3 flex flex-col gap-2">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@company.com"
          aria-label="Your email"
          maxLength={REQUEST_EMAIL_MAX}
          className="w-full rounded-xl border border-border px-3 py-2 text-[16px] focus:outline-none focus:ring-1 focus:ring-brand/30"
        />
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Optional: the project you're trying to build there — this becomes the research brief."
          aria-label="What you're building"
          maxLength={REQUEST_NOTE_MAX}
          rows={2}
          className="w-full rounded-xl border border-border px-3 py-2 text-[15px] focus:outline-none focus:ring-1 focus:ring-brand/30"
        />
        {status === "error" && (
          <p className="text-[13px] text-danger">Couldn't record that just now — please try again in a moment.</p>
        )}
        <div className="flex items-center gap-3">
          <button
            onClick={submit}
            disabled={!valid || status === "sending"}
            className="px-4 py-1.5 bg-brand text-on-solid rounded-xl text-[15px] font-medium disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {status === "sending" ? "Sending…" : "Request this country"}
          </button>
          <button onClick={onClose} className="text-[14px] text-text-secondary hover:text-text-primary">Cancel</button>
        </div>
      </div>
    </div>
  );
}
